/** The speed-unit preference as React state, agreed across a page.
 *
 * Two readouts on one screen must not disagree, so a change made through one
 * hook is announced on `window` and every mounted hook follows. The
 * announcement is a custom event rather than the `storage` event, which
 * browsers fire only in *other* tabs. Reading and writing the browser's
 * storage is `lib/units.ts`'s business, guards included.
 */

import { useCallback, useEffect, useState } from 'react';

import { otherSpeedUnit, readSpeedUnit, writeSpeedUnit } from './units';
import type { SpeedUnit } from './units';

const EVENT = 'gt7sync:speed-unit';

export interface SpeedUnitChoice {
  unit: SpeedUnit;
  /** The unit a click would switch to, for the control's label. */
  other: SpeedUnit;
  choose: (next: SpeedUnit) => void;
  toggle: () => void;
}

export function useSpeedUnit(): SpeedUnitChoice {
  const [unit, setUnit] = useState<SpeedUnit>(() => readSpeedUnit());

  useEffect(() => {
    const follow = (event: Event) => {
      const next = (event as CustomEvent<SpeedUnit>).detail;
      if (next) setUnit(next);
    };
    window.addEventListener(EVENT, follow);
    return () => window.removeEventListener(EVENT, follow);
  }, []);

  const choose = useCallback((next: SpeedUnit) => {
    setUnit(next);
    writeSpeedUnit(next);
    window.dispatchEvent(new CustomEvent<SpeedUnit>(EVENT, { detail: next }));
  }, []);

  const toggle = useCallback(() => choose(otherSpeedUnit(unit)), [choose, unit]);

  return { unit, other: otherSpeedUnit(unit), choose, toggle };
}
